<script setup lang="ts">
import { useForm } from 'vee-validate';

import { auStates, unaccreditedBrokerAggregators } from '@/helpers/const';
import { useUserQuoteProcess } from '@/helpers/useUserQuoteProcess';
import { defaultUnaccreditedBrokerProfile } from '@/helpers/builder';
import { onlyValidPhoneKeys } from '@/helpers/noWhiteSpaceKeys';
import { unaccreditedBrokerSchema } from '@/validation/schemas/v2/unaccredited-broker';

import type { UnaccreditedBrokerProfile } from '@/types/UnaccreditedBrokerProfile';

const { state, loanApp, saveBrokerAndGoNext, goBack } = useUserQuoteProcess();

const { meta, submitCount, values, handleSubmit } = useForm<UnaccreditedBrokerProfile>({
  validationSchema: unaccreditedBrokerSchema(),
  initialValues: {
    ...(state.value.context.unaccreditedBroker ?? defaultUnaccreditedBrokerProfile()),
    ...(loanApp.value.brokerEmail && { email: loanApp.value.brokerEmail }),
  },
  validateOnMount: false,
});

/**
 * Handles the form submission by calling the new dedicated save function.
 * @param {UnaccreditedBrokerProfile} formValues - The validated data from the form.
 */
const onSubmit = handleSubmit(async (formValues) => {
  saveBrokerAndGoNext(formValues);
});
</script>

<template>
  <div class="mb-4 pb-2">
    <h2 class="text-2xl font-medium">
      Provide
      <BxHighlight>your details</BxHighlight>
    </h2>
  </div>
  <div class="min-w-[350px] px-6 py-6">
    <form @submit="onSubmit">
      <BxExpander :model-value="true" separator>
        <template #header>
          <div class="px-4 py-5 sm:px-6">
            <div class="flex flex-wrap items-center justify-between sm:flex-nowrap">
              <h3 class="flex items-center text-lg font-medium leading-6 text-gray-900">
                <span class="mr-3 hidden md:block"><BxIcon name="circleInfo" /></span>
                <span>Broker details</span>
              </h3>
            </div>
          </div>
        </template>
        <template #body>
          <div class="p-6">
            <BxField v-slot="{ field }" name="email">
              <BxInput
                id="unaccredited-broker-email"
                v-bind="field"
                class="form-control"
                inputmode="email"
                label="Email address"
                label-type="floating"
                disabled
              />
            </BxField>
            <BxField v-slot="{ field }" name="firstName">
              <BxInput
                :id="'-first-name'"
                v-bind="field"
                class="form-control"
                label="First name"
                label-type="floating"
              />
            </BxField>
            <BxField v-slot="{ field }" name="lastName">
              <BxInput :id="'-last-name'" v-bind="field" class="form-control" label="Last name" label-type="floating" />
            </BxField>
            <BxField v-slot="{ field }" name="mobile">
              <BxInput
                id="unaccredited-broker-mobile-phone-number"
                v-bind="field"
                class="form-control"
                inputmode="tel"
                label="Mobile number"
                label-type="floating"
                minlength="10"
                :maxlength="values.mobile?.startsWith('+') ? 15 : 10"
                @keypress="onlyValidPhoneKeys"
              />
            </BxField>
            <BxField v-slot="{ field }" name="aggregator">
              <BxSelect
                v-bind="field"
                id="unaccredited-broker-aggregator"
                :list-items="unaccreditedBrokerAggregators"
                class="form-control"
                label="Aggregator"
                label-type="floating"
              />
            </BxField>
            <BxField v-slot="{ field }" name="state">
              <BxSelect
                v-bind="field"
                id="unaccredited-broker-state"
                :list-items="auStates"
                class="form-control"
                label="State"
                label-type="floating"
              />
            </BxField>
            <BxErrorPanel v-show="!meta.valid && submitCount > 0" class="mt-6">
              Please review the error messages above.
            </BxErrorPanel>
            <div class="mt-6">
              <BxButton id="unaccredited-broker-continue" variant="primary" type="submit">Save and continue</BxButton>
              <BxButton v-if="state.nextEvents.includes('BACK')" variant="link" class="ml-4" @click="goBack()">
                Back
              </BxButton>
            </div>
          </div>
        </template>
      </BxExpander>
    </form>
  </div>
</template>
